'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ExternalLink, InfoIcon, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  type PostbackProviderId,
  getPostbackProviderMeta,
} from '@/lib/postback-tracker-meta'

// ============================================================================
// Schema
// ============================================================================

const postbackTrackerFormSchema = z.object({
  trackingDomain: z
    .string()
    .trim()
    .min(1, 'Tracking domain is required')
    .refine(
      (v) => !v.startsWith('http://') && !v.startsWith('https://'),
      'Drop the "https://" prefix — domain only',
    ),
  conversionType: z.string().trim().optional(),
  // Optional fixed-payout override.
  //
  // The HTML <input type="number" valueAsNumber> coerces an empty
  // input to `NaN`, and Zod's `z.number()` rejects `NaN` by default
  // ("Expected number, received nan"). The DB-side default can also
  // be `null` (JSONB field). We funnel all of those into `undefined`
  // BEFORE the number validator runs so the field truly behaves as
  // optional.
  payoutValue: z.preprocess(
    (v) => {
      if (v === undefined || v === null || v === '') return undefined
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) ? n : undefined
    },
    z.number().min(0, 'Payout must be ≥ 0').optional(),
  ),
})

type FormValues = z.infer<typeof postbackTrackerFormSchema>

interface PostbackTrackerFormProps {
  provider: PostbackProviderId
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function PostbackTrackerForm({
  provider,
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: PostbackTrackerFormProps) {
  const { t } = useTranslation('integrations')
  const meta = getPostbackProviderMeta(provider)

  const form = useForm<FormValues>({
    resolver: zodResolver(postbackTrackerFormSchema) as any,
    mode: 'all',
    defaultValues: {
      trackingDomain: defaultValues.settings?.trackingDomain ?? '',
      conversionType: defaultValues.settings?.conversionType ?? '',
      // Coerce null → undefined so the <input> renders empty instead
      // of warning about "value=null" on a controlled input.
      payoutValue: defaultValues.settings?.payoutValue ?? undefined,
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form

  const handleFormSubmit = async (data: FormValues) => {
    const payload: Record<string, any> = {
      trackingDomain: data.trackingDomain.trim(),
    }
    if (data.conversionType && data.conversionType.length > 0) {
      payload.conversionType = data.conversionType.trim()
    }
    if (typeof data.payoutValue === 'number') {
      payload.payoutValue = data.payoutValue
    }
    await onSubmit({
      credentials: {},
      settings: payload,
    })
  }

  return (
    <form
      id="integration-config-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-6"
    >
      {/* About */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>
          {t('postbackTracker.aboutTitle', { name: meta.displayName })}
        </AlertTitle>
        <AlertDescription>
          {t('postbackTracker.aboutDescription')}
        </AlertDescription>
      </Alert>

      {/* Tracking domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('postbackTracker.trackingDomainLabel')}
          </CardTitle>
          <CardDescription>{meta.trackingDomainHint}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="trackingDomain">
            {t('postbackTracker.domainField')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="trackingDomain"
            placeholder={meta.trackingDomainPlaceholder}
            disabled={isLoading}
            {...register('trackingDomain')}
          />
          {errors.trackingDomain && (
            <p className="text-xs text-destructive">{errors.trackingDomain.message as string}</p>
          )}
        </CardContent>
      </Card>

      {/* Optional overrides */}
      <Card>
        <CardHeader>
          <CardTitle>{t('postbackTracker.advancedTitle')}</CardTitle>
          <CardDescription>
            {t('postbackTracker.advancedDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conversionType">
              {t('postbackTracker.conversionTypeLabel')}
            </Label>
            <Input
              id="conversionType"
              placeholder={meta.defaultConversionType}
              disabled={isLoading}
              {...register('conversionType')}
            />
            <p className="text-xs text-muted-foreground">
              {t('postbackTracker.conversionTypeHelp', { default: meta.defaultConversionType })}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="payoutValue">
              {t('postbackTracker.payoutLabel')}
            </Label>
            <Input
              id="payoutValue"
              type="number"
              step="0.01"
              min="0"
              placeholder={t('postbackTracker.payoutPlaceholder')}
              disabled={isLoading}
              {...(register('payoutValue', { valueAsNumber: true }) as any)}
            />
            <p className="text-xs text-muted-foreground">
              {t('postbackTracker.payoutHelp')}
            </p>
            {errors.payoutValue && (
              <p className="text-xs text-destructive">{errors.payoutValue.message as string}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* What we read for click_id */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('postbackTracker.identityTitle')}
          </CardTitle>
          <CardDescription>
            {t('postbackTracker.identityDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-inside list-decimal space-y-1 text-sm text-muted-foreground">
            {meta.clickIdReads.map((read) => (
              <li key={read}>
                <code className="rounded bg-muted px-1 py-0.5">{read}</code>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>{t('postbackTracker.previewTitle')}</CardTitle>
          <CardDescription>
            {t('postbackTracker.previewDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs">
            <code>{meta.urlTemplatePreview}</code>
          </pre>
        </CardContent>
      </Card>

      {/* Docs link */}
      <div>
        <a
          href={meta.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary underline"
        >
          {t('postbackTracker.officialDocs', {
            name: meta.displayName,
          })}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </form>
  )
}
